// Spacing tokens
export const spacing = {
  section: 'scroll-mt-2 py-10 sm:py-12 xl:py-20',
  container: 'w-full flex flex-col max-w-[1440px] mx-auto px-4 gap-8 sm:px-8 sm:gap-10 xl:px-16',
  filledContainer: 'w-full flex flex-col px-3 py-6 gap-8 rounded-3xl sm:px-8 sm:py-12 sm:gap-10 md:rounded-[48px] xl:px-16 xl:py-20',
  block: 'gap-6 sm:gap-8 lg:gap-10',
  inner: 'gap-3 sm:gap-4'
}

export const baseText = `text-pretty font-sans font-medium block max-w-[80ch]`

// Typography tokens
export const typography = {
  // Headings
  xl: `${baseText} leading-tight text-2xl sm:text-5xl lg:text-6xl block 2xl:text-8xl`,
  h1: `${baseText} leading-tight text-3xl sm:text-5xl lg:text-6xl`,
  h2: `${baseText} leading-tight text-3xl sm:text-4xl xl:text-5xl`,
  h3: `${baseText} leading-tight text-2xl sm:text-3xl`,
  h4: `${baseText} leading-tight text-xl sm:text-2xl`,
  h5: `${baseText} leading-normal text-lg sm:text-xl`,
  h6: `${baseText} leading-normal text-base sm:text-lg`,

  // Alt Headings
  a1: 'font-alt font-variations font-medium leading-tight text-4xl sm:text-5xl lg:text-5xl lg:leading-snug lg:tracking-tighter',
  a2: 'font-alt font-variations font-medium leading-tight text-3xl sm:text-4xl lg:leading-snug lg:tracking-tighter',
  a3: 'font-alt font-variations font-medium leading-tight text-2xl sm:text-3xl lg:leading-snug lg:tracking-tighter',
  
  // Body text
  bodyLarge: 'text-lg font-light leading-snug lg:text-2xl',
  body: 'text-base font-light leading-snug sm:text-lg',
  bodySmall: 'text-base font-light leading-snug',
  
  // Special text styles
  captionLarge: 'font-mono text-xl font-bold leading-tight uppercase md:text-2xl md:leading-snug',
  caption: 'font-mono text-lg font-bold leading-tight uppercase md:text-xl md:leading-snug',
  captionSmall: 'font-mono text-base font-bold leading-tight uppercase',
}

const buttonBase = `
  // Base styles
  w-full h-fit
  flex justify-center items-center
  gap-2.5 whitespace-nowrap text-center
  
  // Typography
  font-mono font-bold leading-none
  text-base tracking-wide uppercase
  
  // Effects
  transition-all duration-200 ease-in-out cursor-pointer
  
  // Focus & Disabled states
  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black
  disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:transform-none
  disabled:shadow-none disabled:hover:shadow-none
  disabled:hover:bg-opacity-100
  disabled:hover:bg-inherit
  
  // Motion preferences
  motion-reduce:transition-none motion-reduce:hover:transform-none
`.trim()

const buttonFilled = `
  ${buttonBase}
  rounded-lg
  border-2 border-black
  w-full sm:w-fit
  
  // Hover animation
  hover:transform hover:-translate-x-0.5 hover:-translate-y-0.5
  hover:shadow-[2px_3px_0px_rgba(1,1,1,0.1)]
`.trim()

export const buttons = {
  primary: `
    ${buttonFilled}
    bg-black text-white
    hover:bg-green-dark hover:text-black
    ${typography.caption} px-5 py-4 sm:min-w-[254px] sm:py-4 sm:px-10
  `.trim(),

  primarySmall: `
    ${buttonFilled}
    bg-black text-white
    hover:bg-green-dark hover:text-black
    ${typography.captionSmall} px-5 py-3
  `.trim(),

  secondary: `
    ${buttonFilled}
    bg-white text-black
    hover:bg-green
    ${typography.caption} px-5 py-4 sm:min-w-[254px] sm:py-4 sm:px-10
  `.trim(),

  secondarySmall: `
    ${buttonFilled}
    bg-white text-black
    hover:bg-green
    ${typography.captionSmall} px-5 py-3
  `.trim(),

  ghost: `
    ${buttonFilled}
    bg-white/20 backdrop-blur-[10px] text-black
    hover:bg-green
    ${typography.caption} px-5 py-4 sm:min-w-[254px] sm:py-4 sm:px-10
  `.trim(),

  ghostSmall: `
    ${buttonFilled}
    bg-white/20 backdrop-blur-[10px] text-black
    hover:bg-green
    ${typography.captionSmall} px-5 py-3
  `.trim(),
  
  text: `
    ${buttonBase}
    px-1
    border-b-2 border-black
    hover:italic
    md:text-lg md:font-semibold
    focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-black
    ${typography.caption}
  `.trim(),
}

export const borders = {
  lg: 'border-2 border-black rounded-3xl md:rounded-[48px]',
  md: 'border-2 border-black rounded-[32px]',
  sm: 'border-2 border-black rounded-3xl',
  xs: 'border border-black rounded-2xl',
  full: 'border-2 border-black rounded-full',
}

// Form tokens
export const forms = {
  input: 'w-full py-3 px-4 bg-transparent rounded-2xl focus:bg-gray hover:bg-gray transition-colors',
  label: 'block text-base leading-snug w-full text-left',
  textarea: 'w-full py-3 px-4 bg-transparent rounded-2xl focus:bg-gray hover:bg-gray transition-colors',
  range: 'appearance-none h-px w-full absolute bg-black pointer-events-none left-0 top-0'
}